import { Button, Form, FormTitle, message, Modal, Radio, Select, SubContent, Tag } from '@maxtropy/components';
import { Col, Row, Space, Spin } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import BindUnitModal, { AnalysisUnit } from '../BindUnitModal';
import styles from './index.module.scss';
import {
  apiV2ComprehensiveLabelPagePost,
  apiV2EnergyAnalysisListByOuPost,
  apiV2ScadaListOwnedPost,
  apiV2UetEmtProcessListPost,
  apiV2UetTopologyPost,
  V2ComprehensiveLabelPagePostResponse,
  V2ScadaListOwnedPostResponse,
  V2UetEmtProcessListPostResponse,
  V2UetTopologyPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { getDataQueryPeriodList, GetDataQueryPeriodListRes } from '@/api/energyOverview';
import { fetchWaterConfigDetail, onFinish } from './util';
import { ConfigType, NodeInfo } from './type';
import EnergyFlowRow from './components/EnergyFlowRow';

const WaterConfig = () => {
  const [form] = Form.useForm();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [currentConfigType, setCurrentConfigType] = useState<ConfigType | ''>(''); // 当前用能单元类型，用于区分独立保存，仅限两类
  const [refresh, setRefresh] = useState<boolean>(false);
  const [unitGroup, setUnitGroup] = useState<AnalysisUnit[]>([]); // 用能分析组列表
  const [deviceLabels, setDeviceLabels] = useState<V2ComprehensiveLabelPagePostResponse['list']>([]); // 设备标签列表
  const [uetTopology, setUetTopology] = useState<V2UetTopologyPostResponse['list']>([]); //能源介质拓扑列表
  const [nodeSelect, setNodeSelect] = useState<V2UetEmtProcessListPostResponse['processVos']>([]); //节点选择
  const [currentUnitData, setCurrentUnitData] = useState<Record<ConfigType, number | undefined>>({
    totalWater: undefined,
    sewageStation: undefined,
    totalWaterEnergyGroupId: undefined,
    sewageStationEnergyGroupId: undefined,
  }); // 用更合适的数据结构按 configType 存储选中的 unitId
  const [nonWorkingPeriods, setNonWorkingPeriods] = useState<GetDataQueryPeriodListRes>(); //非工作时段
  const [waterConfiguration, setWaterConfiguration] = useState<V2ScadaListOwnedPostResponse['list']>(); //水务地图选择组态
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [mediumLoadingCount, setMediumLoadingCount] = useState<number>(0);
  const [detailLoading, setDetailLoading] = useState<boolean>(true);
  const [optionsLoading, setOptionsLoading] = useState<boolean>(true);
  const [nodeLoading, setNodeLoading] = useState<boolean>(false);
  const watchedTopology = Form.useWatch('energyTopology', form);

  const handleMediumLoadingChange = useCallback((loading: boolean) => {
    setMediumLoadingCount(count => Math.max(loading ? count + 1 : count - 1, 0));
  }, []);

  //数据回显
  useEffect(() => {
    // 获取水务配置详情
    setDetailLoading(true);
    fetchWaterConfigDetail(form, setCurrentUnitData)
      .then(res => {
        console.log('水务配置详情:', res);
      })
      .catch(() => undefined)
      .finally(() => setDetailLoading(false));
  }, [refresh]);

  useEffect(() => {
    setOptionsLoading(true);
    Promise.all([
      // 获取用能分析组
      apiV2EnergyAnalysisListByOuPost({}).then(res => {
        setUnitGroup((res.list ?? []) as AnalysisUnit[]);
      }),
      // 获取设备标签
      apiV2ComprehensiveLabelPagePost({
        page: 1,
        size: 100,
        type: 3,
      }).then(res => {
        setDeviceLabels(res.list || []);
      }),
      // 获取非工作时段
      getDataQueryPeriodList({}).then((res: GetDataQueryPeriodListRes) => {
        setNonWorkingPeriods(res);
      }),
      // 获取能源介质拓扑
      apiV2UetTopologyPost({}).then(res => {
        setUetTopology(res.list || []);
        console.log('能源介质拓扑列表:', res.list);
      }),
      //选择组态
      apiV2ScadaListOwnedPost({}).then(res => {
        setWaterConfiguration(res.list || []);
      }),
    ])
      .catch(() => undefined)
      .finally(() => setOptionsLoading(false));
  }, []);

  useEffect(() => {
    if (watchedTopology) {
      // 获取节点选择列表
      setNodeLoading(true);
      apiV2UetEmtProcessListPost({ id: watchedTopology })
        .then(res => {
          setNodeSelect(res.processVos || []);
        })
        .catch(() => undefined)
        .finally(() => setNodeLoading(false));
    } else {
      setNodeSelect([]);
    }
  }, [watchedTopology]);

  const handlebindUnit = (nodeInfo: NodeInfo) => {
    // 仅在 currentConfigType 为有效类型时进行保存
    console.log('nodeInfo :>> ', nodeInfo);
    if (!currentConfigType) return;
    const id = nodeInfo.key != null ? Number(nodeInfo.key) : undefined;
    setCurrentUnitData(prev => ({
      ...prev,
      [currentConfigType]: id,
      [`${currentConfigType}EnergyGroupId`]: nodeInfo.group ?? undefined,
    }));
    if (currentConfigType === 'totalWater') form.setFieldValue('energyUnitName1', nodeInfo.label);
    if (currentConfigType === 'sewageStation') form.setFieldValue('energyUnitName2', nodeInfo.label);
  };

  return (
    <Spin
      spinning={detailLoading || optionsLoading || nodeLoading || saveLoading || mediumLoadingCount > 0}
      wrapperClassName={styles.pageSpin}
    >
      <Form
        form={form}
        layout="horizontal"
        initialValues={{ symbolDisplay: true }}
        onFinish={values => {
          setSaveLoading(true);
          onFinish(values, currentUnitData, setIsEdit, setRefresh, message).finally(() => setSaveLoading(false));
        }}
      >
        <div style={{ padding: 20 }}>
          <FormTitle title="厂区水务配置" />
          <SubContent title="全厂总用水配置">
            <Row gutter={8}>
              <Col span={12}>
                <span className={styles.linkText}>选择用能单元：</span>
                <Button
                  type="link"
                  onClick={() => {
                    setCurrentConfigType('totalWater');
                  }}
                  disabled={!isEdit}
                  style={{ marginLeft: '20px' }}
                >
                  配置
                </Button>
                {form.getFieldValue('energyUnitName1') && (
                  <Tag
                    closable={isEdit}
                    onClose={() => {
                      form.setFieldsValue({ energyUnitName1: undefined });
                      currentUnitData['totalWater'] = undefined;
                    }}
                  >
                    {form.getFieldValue('energyUnitName1')}
                  </Tag>
                )}
              </Col>
              <Col span={12}>
                <Form.Item name="unWorkTime" label="非工作时段选择：">
                  <Select
                    disabled={!isEdit}
                    placeholder="请选择"
                    options={nonWorkingPeriods?.dataPeriodList?.map(i => ({ label: i.name, value: i.id }))}
                    allowClear={true}
                  />
                </Form.Item>
              </Col>
            </Row>
          </SubContent>
          <SubContent title="污水站用量配置">
            <Row gutter={8}>
              <Col span={12}>
                <span className={styles.linkText}>选择用能单元：</span>
                <Button
                  type="link"
                  onClick={() => {
                    setCurrentConfigType('sewageStation');
                  }}
                  disabled={!isEdit}
                  style={{ marginLeft: '20px' }}
                >
                  配置
                </Button>
                {form.getFieldValue('energyUnitName2') && (
                  <Tag
                    closable={isEdit}
                    onClose={() => {
                      form.setFieldsValue({ energyUnitName2: undefined });
                      currentUnitData['sewageStation'] = undefined;
                    }}
                  >
                    {form.getFieldValue('energyUnitName2')}
                  </Tag>
                )}
              </Col>
            </Row>
          </SubContent>
          <SubContent title="地图配置">
            <Form.Item name="waterConfig" label="水务选择组态">
              <Select
                placeholder="请选择"
                options={waterConfiguration?.map((i: any) => ({ label: i.name, value: i.id }))}
                disabled={!isEdit}
                style={{ maxWidth: '472px' }}
                allowClear={true}
              />
            </Form.Item>
          </SubContent>
          <SubContent title="用水能流配置">
            <div style={{ marginBottom: '6px' }}>
              <Row gutter={44}>
                <Col flex="0 0 345px">
                  <div className={styles.energyAnalysisGroupTitle}>用能分析组选择</div>
                </Col>
                <Col flex="0 0 345px">
                  <div className={styles.energyAnalysisGroupTitle}>展示层级</div>
                </Col>
                <Col flex="0 0 345px">
                  <div className={styles.energyAnalysisGroupTitle}>介质选择</div>
                </Col>
                <Col flex="0 0 345px">
                  <div className={styles.energyAnalysisGroupTitle}>展示用能分析组名称</div>
                </Col>
              </Row>
            </div>
            <Row gutter={44}>
              {[1, 2, 3].map(index => (
                <EnergyFlowRow
                  key={index}
                  index={index}
                  isEdit={isEdit}
                  unitGroup={unitGroup}
                  onLoadingChange={handleMediumLoadingChange}
                />
              ))}
            </Row>
          </SubContent>
          <SubContent title="管损配置">
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item
                  name="energyTopology"
                  label={<span style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>能源介质拓扑选择</span>}
                  rules={[{ required: true }]}
                >
                  <Select
                    disabled={!isEdit}
                    placeholder="请选择"
                    options={uetTopology?.map(i => ({ label: i.name, value: i.id }))}
                    showSearch
                    allowClear={true}
                    optionFilterProp="label"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="pipeLossNode" label="节点选择：" style={{ flex: 0.77 }} rules={[{ required: true }]}>
                  <Select
                    disabled={!isEdit}
                    placeholder="请选择"
                    options={nodeSelect?.map(i => ({ label: i.name, value: i.id }))}
                    showSearch
                    mode="multiple"
                    allowClear={true}
                    maxTagCount={3}
                    optionFilterProp="label"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="symbolDisplay" label="符号是否展示">
              <Radio.Group disabled={!isEdit}>
                <Radio value={true}>是</Radio>
                <Radio value={false}>否</Radio>
              </Radio.Group>
            </Form.Item>
          </SubContent>
          <SubContent title="报警统计配置">
            <Form.Item name="deviceLabel" label="设备标签" rules={[{ required: true }]}>
              <Select
                placeholder="请选择设备标签"
                options={deviceLabels?.map(i => ({ label: i.name, value: i.id }))}
                showSearch
                optionFilterProp="label"
                mode="multiple"
                disabled={!isEdit}
                style={{ maxWidth: '472px' }}
                allowClear={true}
              />
            </Form.Item>
          </SubContent>
        </div>
      </Form>
      <Space className="sticky-footer" size={8}>
        {!isEdit ? (
          <>
            <Button
              type="primary"
              onClick={() => {
                setIsEdit(true);
              }}
            >
              编辑
            </Button>
          </>
        ) : (
          <Space>
            <Button
              type="primary"
              onClick={() => {
                form.submit();
              }}
            >
              保存
            </Button>
            <Button
              onClick={() => {
                if (isEdit) {
                  Modal.confirm({
                    title: '是否放弃所有未保存信息？',
                    onOk: () => {
                      form.resetFields();
                      setRefresh(prev => !prev);
                      setIsEdit(false);
                    },
                  });
                }
              }}
            >
              取消
            </Button>
          </Space>
        )}
      </Space>
      {currentConfigType && (
        <BindUnitModal
          setOpen={v => setCurrentConfigType(v ? currentConfigType : '')}
          onUnitIdSelect={handlebindUnit}
          linkedId={currentUnitData[currentConfigType]}
          linkedEnergyGroupId={
            currentConfigType === 'totalWater'
              ? currentUnitData['totalWaterEnergyGroupId']
              : currentUnitData['sewageStationEnergyGroupId']
          }
        />
      )}
    </Spin>
  );
};

export default WaterConfig;
